import React, { useState, useRef, useEffect } from "react";
import { Input, useFormControl } from "@chakra-ui/react";

type Props = {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  placeholder?: string;
  w?: string | number;
  max?: number;
  min?: number;
  decimalPlaces?: number;
  isDisabled?: boolean;
  isInvalid?: boolean;
  bg?: string;
  borderColor?: string;
  size?: "sm" | "md" | "lg";
  /** 空入力時に設定するデフォルト値（指定するとnullの代わりにこの値が使用される） */
  emptyValue?: number;
};

type InputType = "number" | "text";

const format = (inputType: InputType, value: number | null) => {
  if (value === null) {
    return "";
  }
  switch (inputType) {
    case "number":
      return value.toString();
    case "text":
      const formatter = new Intl.NumberFormat("ja-JP", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 20,
      });
      return formatter.format(value);
  }
};

// カンマ区切り表示を行うテキストボックス
// onFocus:カンマなし、onBlur:カンマあり
// データはカンマなしで保持
export const CustomNumberInput = (props: Props) => {
  const [inputType, setInputType] = useState<InputType>("text");
  const [inputValue, setInputValue] = useState<string>("");

  // FormControlのコンテキストから状態を取得（親がFormControlの場合に自動継承）
  const formControl = useFormControl({});

  const inputRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => {
    setInputType("number");
    if (props.value !== null) {
      setInputValue(props.value.toString());
    }
  };

  const handleBlur = () => {
    setInputType("text");
    // 空入力時にemptyValueが指定されていればその値を使用
    const parsedValue = inputValue === "" ? null : parseFloat(inputValue.replace(/,/g, ""));
    const numericValue = parsedValue === null && props.emptyValue !== undefined ? props.emptyValue : parsedValue;
    if (props.onBlur) {
      props.onBlur(numericValue);
    }
    setInputValue(format("text", numericValue ?? props.value));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value === "" ? null : parseFloat(value.replace(/,/g, ""));

    // 小数点以下の桁数のチェック（デフォルト0）
    const decimalPlaces = props.decimalPlaces ?? 0;
    const regex = new RegExp(`^-?\\d*(\\.\\d{0,${decimalPlaces}})?$`);
    if (!regex.test(value)) {
      return; // 小数点以下の桁数が範囲外の場合は値を設定しない
    }

    // 桁数のチェック
    if (numericValue !== null && props.max !== undefined && props.min !== undefined) {
      if (numericValue > props.max || numericValue < props.min) {
        return; // 桁数が範囲外の場合は値を設定しない
      }
    }

    setInputValue(value);
    props.onChange(numericValue);
  };

  const format = (type: "text" | "number", value: number | null): string => {
    if (value === null || value === undefined || type === "number") {
      return value === null || value === undefined ? "" : value.toString();
    }
    // decimalPlacesが指定されている場合はその桁数まで表示（デフォルト0）
    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: props.decimalPlaces ?? 0,
    };
    return value.toLocaleString("ja-JP", options);
  };

  useEffect(() => {
    setInputValue(format(inputType, props.value));
  }, [inputType, props.value]);

  return (
    <Input
      ref={inputRef}
      type={inputType === "number" ? "number" : "text"} // フォーカス時に数値入力を許可
      value={inputValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      textAlign="right"
      isDisabled={props.isDisabled ?? formControl.disabled}
      isInvalid={props.isInvalid ?? formControl["aria-invalid"] === true}
      placeholder={props.placeholder}
      width={props.w}
      bg={props.bg}
      borderColor={props.borderColor ?? "#E2E8F0"}
      size={props.size ?? "md"}
    />
  );
};
